import { useEffect } from 'react';
import { DOCUMENT_TYPE_BY_CODE, deepLink } from '@erp-attach/shared';
import { useApi } from '../api/ApiProvider';
import type { DocumentSummary } from '../api/types';
import { config } from '../config';
import { formatBytes, formatDate, formatDateTime, shortUpn } from '../lib/format';
import { saveBlob } from '../lib/saveBlob';
import { useDocumentPreview } from '../hooks/useDocumentPreview';
import { Button } from './ui/Button';
import { Chip, StatusChip } from './ui/Chip';
import { CopyButton } from './CopyButton';
import { DocumentPreview } from './DocumentPreview';
import { FilePlate } from './FilePlate';
import { RecordsActions } from './RecordsActions';

export interface DocumentPeekProps {
  doc: DocumentSummary;
  onClose: () => void;
  onOpen: (doc: DocumentSummary) => void;
  onChanged?: () => void;
}

/** Right-hand preview panel for the selected file. Escape closes it. */
export function DocumentPeek({ doc, onClose, onOpen, onChanged }: DocumentPeekProps) {
  const api = useApi();
  const dt = DOCUMENT_TYPE_BY_CODE.get(doc.documentTypeCode);
  const v = doc.currentVersion;
  const canRead = doc.actions.includes('read');
  const preview = useDocumentPreview({ id: doc.id, status: doc.status, hidden: doc.hidden, canRead });
  const viewable = preview.kind === 'pdf' || preview.kind === 'image';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = async () => {
    const { blob, filename } = await api.documents.download(doc.id);
    saveBlob(blob, filename ?? v?.originalFilename ?? doc.title);
  };

  return (
    <aside className="gsl-workspace-peek" aria-label={`Preview of ${doc.title}`}>
      <header className="flex items-start gap-3 border-b border-border px-4 py-3">
        <FilePlate filename={v?.originalFilename} mime={v?.detectedMime} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-sans text-sm font-semibold tracking-normal" title={doc.title}>
            {doc.title}
          </h2>
          <p className="truncate text-xs text-muted-foreground" title={v?.originalFilename}>
            {v?.originalFilename ?? 'No file yet'}
          </p>
        </div>
        <Button variant="ghost" size="sm" icon="x" iconOnly aria-label="Close preview" onClick={onClose} />
      </header>

      <div className="flex flex-wrap gap-1.5 border-b border-border px-4 py-2.5">
        <Button size="sm" icon="maximize" onClick={() => onOpen(doc)} disabled={!canRead}>
          Open
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon="download"
          disabled={!viewable && preview.kind !== 'unsupported'}
          onClick={() => void download()}
        >
          Download
        </Button>
        <CopyButton text={deepLink.document(String(doc.docNo), config.appBaseUrl)} size="sm" />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="border-b border-border">
          <DocumentPreview preview={preview} title={doc.title} height="22rem" compact />
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 px-4 py-3 text-xs">
          <dt className="text-muted-foreground">Type</dt>
          <dd className="flex flex-wrap items-center gap-1">
            {dt?.label ?? doc.documentTypeCode}
            {dt?.sensitivity === 'restricted' ? <Chip variant="warning">Restricted</Chip> : null}
          </dd>
          <dt className="text-muted-foreground">Status</dt>
          <dd className="flex flex-wrap gap-1">
            <StatusChip status={doc.status} hidden={doc.hidden} />
            {doc.legalHold ? <Chip variant="danger">Legal hold</Chip> : null}
          </dd>
          <dt className="text-muted-foreground">Size</dt>
          <dd className="gsl-num">{v ? formatBytes(v.sizeBytes) : '—'}</dd>
          <dt className="text-muted-foreground">Version</dt>
          <dd className="gsl-num">{v ? `v${v.versionNo}` : '—'}</dd>
          <dt className="text-muted-foreground">Uploaded</dt>
          <dd>{v ? `${shortUpn(v.uploadedByUpn)}, ${formatDateTime(v.uploadedAt)}` : '—'}</dd>
          {doc.retainUntil ? (
            <>
              <dt className="text-muted-foreground">Retain until</dt>
              <dd>{formatDate(doc.retainUntil)}</dd>
            </>
          ) : null}
        </dl>

        <div className="px-4 pb-4">
          <RecordsActions doc={doc} onChanged={onChanged} compact />
        </div>
      </div>
    </aside>
  );
}
